import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import { type GridColDef } from '@mui/x-data-grid-pro';
import { useModal } from '../../../../../store/ModalProvider';
import CustomButton from '../../CustomButton/CustomButton';
import { AnimatedDataGridPro } from '../../CustomDataGrid';
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx';
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu';
import type {
  AssignSelectedModalData,
  ScheduleModalData,
} from '../../../../../types/modal';
// TODO: Remover mocks quando RTK Query estiver implementado
import servicesMock from '../../../../../data/mockedData/schedules/services.json';
import scheduledServicesMock from '../../../../../data/mockedData/schedules/scheduledServices.json';

type ColumnDefinition = {
  key: string;
  label: string;
  width: string;
};

type RowData = {
  id: string;
  [key: string]: string;
};

interface ScheduleServiceMock {
  id: number;
  startDate: string;
  startTime: string;
  service: string;
  startLocation: string;
  endLocation: string;
  endTime: string;
  endDate: string;
  client: string;
  observations?: string;
  driver?: string;
  vehicle?: string;
}

const compatibleColumns: ColumnDefinition[] = [
  { key: 'dataInicio', label: 'Data Início', width: '110px' },
  { key: 'horaInicio', label: 'Hora Início', width: '90px' },
  { key: 'servico', label: 'Serviço', width: '90px' },
  { key: 'localInicio', label: 'Local Início', width: '120px' },
  { key: 'localTermino', label: 'Local Término', width: '120px' },
  { key: 'horaTermino', label: 'Hora Término', width: '90px' },
  { key: 'dataTermino', label: 'Data Término', width: '110px' },
  { key: 'cliente', label: 'Cliente', width: '160px' },
  { key: 'observacoes', label: 'Observações', width: '140px' },
  { key: 'motorista', label: 'Motorista', width: '120px' },
];

const incompatibleColumns: ColumnDefinition[] = [
  { key: 'incompatibilidade', label: 'Incompatibilidade', width: '180px' },
  { key: 'dataInicio', label: 'Data Início', width: '110px' },
  { key: 'horaInicio', label: 'Hora Início', width: '90px' },
  { key: 'servico', label: 'Serviço', width: '90px' },
  { key: 'localInicio', label: 'Local Início', width: '120px' },
  { key: 'localTermino', label: 'Local Término', width: '120px' },
  { key: 'horaTermino', label: 'Hora Término', width: '90px' },
  { key: 'dataTermino', label: 'Data Término', width: '110px' },
  { key: 'cliente', label: 'Cliente', width: '160px' },
];

const incompatibilityReasons = [
  'Zona; Sobreposição Horário',
  'Restrição Cliente',
  'Tipo de Serviço',
  'Já tem Motorista',
];

const tableSx = {
  border: 'none',
  '--DataGrid-rowBorderColor': 'transparent',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
    borderTop: 'none',
    boxShadow: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    fontSize: '11px',
    color: 'var(--primary-light)',
    whiteSpace: 'nowrap',
  },
  '& .MuiDataGrid-columnSeparator': {
    opacity: 0,
    color: 'rgba(134, 149, 175, 0.6)',
    transition: 'opacity 0.2s ease',
  },
  '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
    opacity: 1,
  },
  '& .MuiDataGrid-columnSeparator--resizable': {
    cursor: 'col-resize',
  },
  '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
    borderRight: 'none',
  },
  '& .MuiDataGrid-cell': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--primary-dark)',
    borderBottom: 'none',
  },
  '& .MuiDataGrid-row': {
    backgroundColor: 'var(--Cinza-BG, #F5F7FC)',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: 'rgba(0, 165, 255, 0.08)',
  },
  '& .MuiDataGrid-row.Mui-selected:hover': {
    backgroundColor: 'rgba(0, 165, 255, 0.12)',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
    outline: 'none',
  },
};

interface ScheduleTableSectionProps {
  title: string;
  selectedCount: number;
  columns: ColumnDefinition[];
  rows: RowData[];
  accentColor?: string;
  selectedRowIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
}

const ScheduleTableSection: React.FC<ScheduleTableSectionProps> = ({
  title,
  selectedCount,
  columns,
  rows,
  accentColor = 'var(--primary-dark)',
  selectedRowIds,
  onSelectionChange,
}) => {
  const isSelectable =
    Array.isArray(selectedRowIds) && typeof onSelectionChange === 'function';
  const gridColumns = useMemo<GridColDef<RowData>[]>(
    () =>
      columns.map((column) => ({
        field: column.key,
        headerName: column.label,
        width: Number.parseInt(column.width, 10),
        renderCell: (params) => params.value ?? '--',
      })),
    [columns],
  );
  const bodyHeight = Math.min(180, rows.length * 32);
  const gridHeight = Math.max(bodyHeight + 40, 120);

  return (
    <Box sx={{ display: 'grid', gap: '12px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              fontFamily: 'Poppins, sans-serif',
              color: accentColor,
            }}
          >
            {title}
          </Typography>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: 'rgba(134, 149, 175, 0.35)' }}
          />
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 500,
              fontFamily: 'Poppins, sans-serif',
              color: accentColor,
            }}
          >
            {selectedCount} selecionado(s)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <IconButton
            size="small"
            sx={{
              borderRadius: '8px',
              border: '1px solid rgba(134, 149, 175, 0.25)',
              color: 'var(--primary-light)',
              backgroundColor: '#FFFFFF',
            }}
          >
            <TableRowsIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            size="small"
            sx={{
              borderRadius: '8px',
              border: '1px solid rgba(134, 149, 175, 0.25)',
              color: 'var(--primary-light)',
              backgroundColor: '#FFFFFF',
            }}
          >
            <ViewHeadlineIcon fontSize="inherit" />
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          borderRadius: '12px',
          backgroundColor: '#FFFFFF',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ height: gridHeight }}>
          <AnimatedDataGridPro
            slots={{ columnMenu: CustomColumnMenu }}
            slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
            rows={rows}
            columns={gridColumns}
            density="compact"
            checkboxSelection={isSelectable}
            rowSelectionModel={isSelectable ? selectedRowIds : undefined}
            onRowSelectionModelChange={(newSelection) => {
              if (isSelectable) {
                onSelectionChange?.(newSelection as string[]);
              }
            }}
            disableRowSelectionOnClick
            hideFooter
            rowHeight={32}
            columnHeaderHeight={40}
            sx={[tableSx, dataGridDensityTransitionSx]}
          />
        </Box>
      </Box>
    </Box>
  );
};

/**
 * ## CustomModalManageSchedule component
 * Modal content for the "Gerir Escala" (Manage Schedule) functionality.
 *
 * This component is rendered inside the CustomModal system and provides
 * the interface for managing schedule filters and configurations.
 *
 * @returns {JSX.Element} The rendered modal content
 */
export const CustomModalManageSchedule: React.FC = () => {
  const { closeModal, modalData, modalHandlers, openModal } = useModal();
  const scheduleData = modalData as ScheduleModalData | undefined;
  const [, handleReset] = modalHandlers ?? [];
  const filters =
    scheduleData?.filters && typeof scheduleData.filters === 'object'
      ? (scheduleData.filters as Record<string, unknown>)
      : {};
  const services = useMemo(() => servicesMock as ScheduleServiceMock[], []);
  const scheduledServices = useMemo(
    () => scheduledServicesMock as ScheduleServiceMock[],
    [],
  );
  const combinedServices = useMemo(
    () => [...services, ...scheduledServices],
    [services, scheduledServices],
  );
  const compatibleRows = useMemo<RowData[]>(() => {
    const base = combinedServices.slice(0, 1);
    return base.map((service) => ({
      id: `compat-${service.id}`,
      dataInicio: service.startDate,
      horaInicio: service.startTime,
      servico: service.service,
      localInicio: service.startLocation,
      localTermino: service.endLocation,
      horaTermino: service.endTime,
      dataTermino: service.endDate,
      cliente: service.client,
      observacoes: service.observations || '--',
      motorista: service.driver || '--',
    }));
  }, [combinedServices]);

  const compatibleRowIds = useMemo(
    () => compatibleRows.map((row) => row.id),
    [compatibleRows],
  );
  const [selectedCompatibleIds, setSelectedCompatibleIds] = useState<string[]>(
    () => compatibleRowIds,
  );
  useEffect(() => {
    setSelectedCompatibleIds((prev) =>
      prev.filter((id) => compatibleRowIds.includes(id)),
    );
  }, [compatibleRowIds]);

  const incompatibleRows = useMemo<RowData[]>(() => {
    const base = combinedServices.slice(1, 5);
    return base.map((service, index) => ({
      id: `incompat-${service.id}`,
      incompatibilidade:
        incompatibilityReasons[index % incompatibilityReasons.length],
      dataInicio: service.startDate,
      horaInicio: service.startTime,
      servico: service.service,
      localInicio: service.startLocation,
      localTermino: service.endLocation,
      horaTermino: service.endTime,
      dataTermino: service.endDate,
      cliente: service.client,
    }));
  }, [combinedServices]);

  const incompatibleRowIds = useMemo(
    () => incompatibleRows.map((row) => row.id),
    [incompatibleRows],
  );
  const [selectedIncompatibleIds, setSelectedIncompatibleIds] = useState<
    string[]
  >(() => incompatibleRowIds);
  useEffect(() => {
    setSelectedIncompatibleIds((prev) =>
      prev.filter((id) => incompatibleRowIds.includes(id)),
    );
  }, [incompatibleRowIds]);

  const driverName =
    typeof filters.driverName === 'string' ? filters.driverName : 'Lucas Ferreira';

  const handleCancelClick = () => {
    if (typeof handleReset === 'function') {
      handleReset(filters);
    }
    closeModal();
  };

  const handleAssignSelectedClick = () => {
    const assignData: AssignSelectedModalData = {
      driverName,
      selectedCount: selectedCompatibleIds.length,
    };

    closeModal();
    setTimeout(() => {
      openModal('Atribuir Selecionados', assignData, []);
    }, 0);
  };

  const hasSelectedRows = selectedCompatibleIds.length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'auto',
        width: '100%',
        height: '100%',
        padding: { xs: '16px', md: '32px' },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: 'min(1200px, 100%)',
          height: { xs: '100%', md: 'calc(100% - 32px)' },
          maxHeight: '760px',
          borderRadius: { xs: '3px', md: '3px' },
          backgroundColor: '#FFFFFF',
          border: '1px solid rgba(134, 149, 175, 0.2)',
          boxShadow: '0 24px 60px rgba(9, 32, 86, 0.16)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: 'none',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                color: 'var(--primary-dark)',
              }}
            >
              Rever Serviços a atribuir - {driverName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            padding: '20px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            overflow: 'auto',
          }}
        >
          <ScheduleTableSection
            title="1 Serviço Compatível"
            selectedCount={selectedCompatibleIds.length}
            columns={compatibleColumns}
            rows={compatibleRows}
            selectedRowIds={selectedCompatibleIds}
            onSelectionChange={setSelectedCompatibleIds}
          />

          <ScheduleTableSection
            title="4 Serviços incompatíveis"
            selectedCount={selectedIncompatibleIds.length}
            columns={incompatibleColumns}
            rows={incompatibleRows}
            accentColor="#092056"
            selectedRowIds={selectedIncompatibleIds}
            onSelectionChange={setSelectedIncompatibleIds}
          />

          <Box sx={{ display: 'grid', gap: '12px' }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                color: 'var(--primary-dark)',
              }}
            >
              Outros Alertas
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                borderRadius: '8px',
                background:
                  'linear-gradient(90deg, rgba(255, 92, 0, 0.95) 0%, rgba(251, 176, 96, 0.95) 100%)',
                color: '#FFFFFF',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              <WarningAmberRoundedIcon fontSize="small" />
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  fontFamily: 'Poppins, sans-serif',
                  color: '#FFFFFF',
                }}
              >
                O Motorista Lucas Ferreira, que pretende atribuir ao(s)
                Serviço(s), encontra-se sem folga há mais de 6 dias.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '16px',
            backgroundColor: '#FFFFFF',
          }}
        >
          <CustomButton
            variant="text"
            color="primary"
            onClick={handleCancelClick}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '6px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              color: 'var(--primary)',
              background: 'transparent',
              boxShadow: 'none',
              '&:hover': {
                background: 'transparent',
                color: 'var(--primary-dark)',
              },
            }}
          >
            Cancelar Atribuição
          </CustomButton>
          <CustomButton
            variant="contained"
            color="primary"
            disabled={!hasSelectedRows}
            onClick={handleAssignSelectedClick}
            sx={{
              height: '32px',
              minHeight: '32px',
              padding: '0 16px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 6px 12px rgba(9, 32, 86, 0.18)',
            }}
          >
            Atribuir Selecionados
          </CustomButton>
        </Box>
      </Paper>
    </Box>
  );
};

export default CustomModalManageSchedule;
export {};

